import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "rhombus-react";
import { useDispatch, useSelector } from "react-redux";
import { destroy, initialize, getFormValues, change } from "redux-form";
import { cloneDeep } from "lodash";

import { useModalStack } from "utils/common";
import useRefinementDataUrl from "hooks/useRefinementDataUrl";
import FramingButton from "widgets/modals/versions/refinement/FramingButton";
import useFramingCheck from "hooks/useFramingCheck";
import AdjustFramingBlade from "widgets/modals/versions/refinement/AdjustFramingBlade";

import { EDIT_VERSION_BLADE_FORM } from "forms/list";
import { AI_GENERATE_VERSIONS_FORM } from "./constants";
import { setActiveVariable, setVariationRefinement } from "reducers/Refinements";
import { getDisplayData } from "selectors/AI";

import {
	renderDisplayPlacement,
	findVariation,
	addSelectedVersion,
	removeSelectedVersion,
	makeVersionWithJCRMAssets
} from "./utils";

import "./VariationPreview.css";

export default function VariationPreview(props) {
	const {
		creativeId,
		variables,
		variationIndex,
		variationName,
		ftUtilsId,
	} = props;

	const dispatch = useDispatch();
	const formValues = useSelector(getFormValues(AI_GENERATE_VERSIONS_FORM));
	const { assetRefinements, variationRefinements, isAssetRefinementsLoading } = useSelector(state => state.refinements);
	const { versionNamePrefix, selectedVariations } = formValues;
	const displayData = useSelector(getDisplayData);
	const data = displayData.find(data => `${data.creative.id}` === `${creativeId}`);
	const libraryId = data?.creative?.creativeLibrary?.id;
	const creativeName = data?.creative?.name || "";
	const isSelected = !!findVariation(selectedVariations[creativeId]?.variations, variables);
	const variationNameIndex = variationName?.replaceAll(" ", "-").toLowerCase();
	const version = data?.creative?.version;
	const [isCropping, setIsCropping] = useState(false);

	const commonModalProps = {
		allowFullScreen: true,
		isFullScreen: false,
		setAppTitle: false,
		type: "blade",
		xClose: true,
	};
	const [, { popModal, pushModal, clearStack }, ModalStackHead] = useModalStack({ commonModalProps });
	const { width, height } = data?.creative || {};
	const dimensions = `${width}x${height}`;
	// Return empty array if no image variable found.
	// As there are cases text only vars are generated
	const [imageVariableName, imageVariableUrl] = useMemo(() => {
		return Object.entries(variables).find(([variableName]) => {
			const variableConfig = version?.[variableName];
			const variableType = variableConfig?.type;
			return (/image/i.test(variableType));
		}) || [];
	}, [variables]);

	// If we have a custom variation refinement for this asset, use it otherwise use the default asset refinement
	const canvas = variationRefinements?.[variationIndex]?.[dimensions] ?? assetRefinements?.[imageVariableUrl]?.[dimensions];
	const variableConfig = version?.[imageVariableName];
	const frame = variableConfig?.cropMetadata?.frame ?? {};

	const { refinementDataUrl } = useRefinementDataUrl({
		src: imageVariableUrl,
		left: canvas?.left,
		top: canvas?.top,
		frameWidth: frame.width,
		frameHeight: frame.height,
		canvasWidth: canvas?.width,
		canvasHeight: canvas?.height,
		opacity: 1,
		// Only does real hard lifting of rendering in canvas when hideImageReference turns true.
		skip: !imageVariableUrl && !canvas,
		shouldUseCredentials: false,
	});

	let showAdjustFramingModal = useFramingCheck(data.creative, data.creative.version);

	useEffect(() => {
		if (isAssetRefinementsLoading) {
			setIsCropping(true);
		}

		// We only reset isCropping when we have a refinementDataUrl calculated
		if (!isAssetRefinementsLoading && refinementDataUrl) {
			setIsCropping(false);
		}
	}, [isAssetRefinementsLoading, refinementDataUrl]);

	function handleChange(event) {
		let newSelections;
		if (event?.target?.checked) {
			newSelections = addSelectedVersion(selectedVariations, creativeId, variables);
		} else {
			newSelections = removeSelectedVersion(selectedVariations, creativeId, variables);
		}
		dispatch(change(AI_GENERATE_VERSIONS_FORM, "selectedVariations", newSelections));
	}

	// We need to render cropped image if one exists
	const previewData = useMemo(() => {
		// If there is no image variable and there is only text or static images
		// We need to calculate preview data. Otherwise do not do
		// do any expensive calculations until binary URL for cropping is calculated
		// Every image will always be cropped, so this is the only URL we have to wait for
		if (!imageVariableName || refinementDataUrl) {
			const generatedData = cloneDeep(data);
			const version = makeVersionWithJCRMAssets(generatedData?.creative?.version);

			Object.entries(variables).forEach(([variableName, variableValue]) => {
				const variableConfig = version?.[variableName];

				if (variableConfig) {
					const variableType = variableConfig?.type;
					if (/text/i.test(variableType)) {
						variableConfig.value = variableValue;
						/**
						 * AdStudio previews expect `data` rather than `value` for text properties
						 */
						variableConfig.data = variableValue;
					} else if (/image/i.test(variableType)) {
						variableConfig.prop = refinementDataUrl;
						variableConfig.cropMetadata = {
							...variableConfig.cropMetadata,
							canvas,
							asset: {
								width: 1024,
								height: 1024,
							},
							hasManualRefinements: true,
						};
						variableConfig.assetUrl = variableValue;
					}
				}
			});

			generatedData.creative.version = version;
			generatedData.placement.id = ftUtilsId;
			return generatedData;
		} else {
			return null;
		}
	}, [data, variables, ftUtilsId, refinementDataUrl]);

	function closeModal() {
		dispatch(destroy(EDIT_VERSION_BLADE_FORM));
		clearStack();
	}

	function saveRefinement(values) {
		const imageVariable = values[imageVariableName];
		const { cropMetadata } = imageVariable;
		const { canvas } = cropMetadata;
		dispatch(setVariationRefinement(variationIndex, dimensions, canvas));
	}

	const openAdjustFramingModal = () => {
		// We need to initialize EDIT_VERSION_BLADE_FORM as popped blade is fully based on it
		dispatch(initialize(EDIT_VERSION_BLADE_FORM, previewData.creative.version));
		// Set the active variable so the AdjustFramingDetailPanel opens
		// withs the correct variable selected
		dispatch(setActiveVariable(imageVariableName));
		function Configured(props) {
			let modalProps = {
				libraryId,
				assetUrl: previewData.assetUrl,
				hidePreviewButton: true,
				versionInfo: {
					name: `${versionNamePrefix} - ${variationName} - ${dimensions}`,
					variableConfig: previewData.creative.version,
				},
				commonModalProps: { ...commonModalProps, closeModal },
				navigationActions: { popModal, pushModal },
				applyUpdates: saveRefinement,
				shouldUseCredentials: false,
			};

			return <AdjustFramingBlade {...modalProps} />;
		}

		pushModal(Configured);
	};

	function reloadPreview() {
		// Only render when there is preview data
		// This is to prevent the preview from rendering
		// before cropping is complete
		if (previewData) {
			renderDisplayPlacement(previewData);
		}
	}

	useEffect(reloadPreview, [previewData]);

	return (
		<>
			{ModalStackHead && <ModalStackHead />}
			<article className="variation-preview-container preview">
				<div className="preview-select centered-row">
					<input
						className={`cypress-gen-ai-select-${variationNameIndex}`}
						type="checkbox"
						id={ftUtilsId}
						checked={isSelected}
						onChange={handleChange}
					/>
					<label htmlFor={ftUtilsId}>Select</label>
				</div>
				<figure className="iframe-container cypress-variation-preview-content" style={{ width: `${width}px`, height: `${height}px` }}>
					{
						isCropping && (<div className="icon-container">
							<i className="ft-ic fa-solid fa-crop fa-beat" />
						</div>)
					}
					<div id={`ftscript_m${ftUtilsId}`} />
				</figure>
				<p className="subheader">{`${creativeName} (${creativeId})`}</p>
				<div className="cypress-variation-preview-name">
					{variationName ? `${versionNamePrefix} - ${variationName} - ` : ""}{dimensions}
				</div>
				<footer className="preview-controls cypress-variation-preview-actions">
					<Icon
						name="far fa-sync-alt"
						className={`version-refresh cypress-gen-ai-refresh-${variationNameIndex}`}
						onClick={reloadPreview} />
					<FramingButton isVisible={showAdjustFramingModal} onClick={openAdjustFramingModal} />
				</footer>
			</article>
		</>
	);
}

VariationPreview.displayName = "VariationPreview";
